import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { AppLayout } from '@/components/Layout/AppLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterTenantPage } from '@/pages/RegisterTenantPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { ProductFormPage } from '@/pages/ProductFormPage';
import { PartiesPage } from '@/pages/PartiesPage';
import { PartyFormPage } from '@/pages/PartyFormPage';
import { TenantPartyFormPage } from '@/pages/TenantPartyFormPage';
import { TenantPartiesBulkPage } from '@/pages/TenantPartiesBulkPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { MyOrdersPage } from '@/pages/MyOrdersPage';
import { MyOrdersPageOld } from '@/pages/MyOrdersPageOld';
import { MyOrderDetailPage } from '@/pages/MyOrderDetailPage';
import { ReceivedOrdersPage } from '@/pages/ReceivedOrdersPage';
import { OrderInboxPage } from '@/pages/OrderInboxPage';
import { ReceivedOrderDetailPage } from '@/pages/ReceivedOrderDetailPage';
import { SalesRepsPage, RepRetailersPage } from '@/pages/SalesRepsPage';
import { InvoicesPage, UsersPage, NotFoundPage } from '@/pages/Placeholders';
import { ReportsPage } from '@/pages/ReportsPage';
import { SubscriptionAdminPage } from '@/pages/SubscriptionAdminPage';
import { MySubscriptionPage } from '@/pages/MySubscriptionPage';
import { AdminTenantsPage } from '@/pages/AdminTenantsPage';
import { AdminCreateTenantPage } from '@/pages/AdminCreateTenantPage';
import { AdminVendorsPage } from '@/pages/AdminVendorsPage';
import { useAuthStore } from '@/stores/authStore';

function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, []);

  function handleLogin() {
    setOpen(false);
    navigate('/login', { replace: true });
  }

  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 700, color: '#152346' }}>Session Expired</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#374151' }}>
          Your session has expired. Please log in again to continue.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleLogin} sx={{ minWidth: 100 }}>
          Login
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <BrowserRouter>
      <SessionExpiredModal />
      <Routes>
        {/* Public */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Intentionally not linked from anywhere in the UI (no nav item, no
            button) — the platform super-admin must type this URL in directly.
            Business type is locked to OTHERS; it's no longer a selectable
            option on the regular /login page. */}
        <Route path="/super-admin-login" element={<LoginPage lockBusinessType="OTHERS" />} />
        <Route path="/register-tenant" element={<RegisterTenantPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* Authenticated, tenant-type-agnostic — every module needs these */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* User-edit-profile page — reached from the avatar
                dropdown's "Edit profile" item. Backed by GET / PUT
                /tenants/me. */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />

            <Route path="/orders" element={<MyOrdersPage />} />
            <Route path="/orders-old" element={<MyOrdersPageOld />} />
            <Route path="/orders/my/:id" element={<MyOrderDetailPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/my-subscription" element={<MySubscriptionPage />} />
          </Route>
        </Route>

        {/* Wholesaler-only — product catalog, the "My Parties" book + its
            forms, order inbox (receiving), and invoices. None of these
            exist as a feature for RETAILER or REPRESENTATIVE tenants —
            role checks alone don't stop them (every tenant's admin user
            only ever has TENANT_ADMIN, independent of tenant type), so
            this gates on tenantType directly. */}
        <Route element={<ProtectedRoute requireTenantType={['WHOLESALER']} />}>
          <Route element={<AppLayout />}>
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/new" element={<ProductFormPage />} />
            <Route path="/products/:id/edit" element={<ProductFormPage />} />

            <Route path="/network/new" element={<PartyFormPage />} />
            <Route path="/tenant-parties/new" element={<TenantPartyFormPage />} />
            <Route path="/tenant-parties/:id/edit" element={<TenantPartyFormPage />} />
            <Route path="/tenant-parties/bulk" element={<TenantPartiesBulkPage />} />

            <Route path="/orders/inbox" element={<OrderInboxPage />} />
            <Route path="/orders/received" element={<Navigate to="/orders/inbox" replace />} />
            <Route path="/orders/received-old" element={<ReceivedOrdersPage />} />
            <Route path="/orders/inbox/:id" element={<ReceivedOrderDetailPage />} />
            <Route path="/orders/received/:id" element={<ReceivedOrderDetailPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
          </Route>
        </Route>

        {/* Parties — Network (wholesaler, full CRUD via the page's own tabs)
            or Suppliers (retailer, browse-only). Not used by REPRESENTATIVE
            tenants, who have their own Suppliers/Retailers pages below. */}
        <Route element={<ProtectedRoute requireTenantType={['WHOLESALER', 'RETAILER']} />}>
          <Route element={<AppLayout />}>
            <Route path="/network" element={<PartiesPage />} />
            <Route path="/parties" element={<Navigate to="/network" replace />} />
          </Route>
        </Route>

        {/* Suppliers (/salesreps) — wholesaler manages its reps, rep browses
            wholesalers. Not used by RETAILER tenants. */}
        <Route element={<ProtectedRoute requireTenantType={['WHOLESALER', 'REPRESENTATIVE']} />}>
          <Route element={<AppLayout />}>
            <Route path="/salesreps" element={<SalesRepsPage />} />
          </Route>
        </Route>

        {/* Retailers (/rep-retailers) — rep-only retailer assignment view. */}
        <Route element={<ProtectedRoute requireTenantType={['REPRESENTATIVE']} />}>
          <Route element={<AppLayout />}>
            <Route path="/rep-retailers" element={<RepRetailersPage />} />
          </Route>
        </Route>

        {/* Authenticated + role-restricted */}
        <Route element={<ProtectedRoute requireAny={['TENANT_ADMIN', 'SUPER_ADMIN']} />}>
          <Route element={<AppLayout />}>
            <Route path="/users" element={<UsersPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute requireAny={['SUPER_ADMIN']} />}>
          <Route element={<AppLayout />}>
            <Route path="/admin/subscription" element={<SubscriptionAdminPage />} />
            <Route path="/admin/tenants" element={<AdminTenantsPage />} />
            <Route path="/admin/tenants/new" element={<AdminCreateTenantPage />} />
            <Route path="/admin/vendors" element={<AdminVendorsPage />} />
          </Route>
        </Route>

        {/* Fallbacks */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
